<script lang="ts">
  import type { Environment } from '../../types/debugger';
  import type { S3Execution, S3FileEntry, ExecutionSummaryData } from '../../services/s3-client';
  import { listExecutions, listFiles, getFileContent, getExecutionSummary, searchExecution } from '../../services/s3-client';
  import { navigate, goHome } from '../../stores/router';
  import ToolPageLayout from '../shared/ToolPageLayout.svelte';
  import EnvSwitcher from '../shared/EnvSwitcher.svelte';
  import JsonViewer from '../shared/JsonViewer.svelte';
  import MarkdownViewer from '../shared/MarkdownViewer.svelte';
  import StatusBadge from '../shared/StatusBadge.svelte';
  import ExecutionList from './ExecutionList.svelte';
  import FileTree from './FileTree.svelte';
  import NodeSummaryCard from './NodeSummaryCard.svelte';

  let env = $state<Environment>('Beta');
  let executions = $state<S3Execution[]>([]);
  let loadingExecs = $state(true);
  let selectedExecId = $state<string | null>(null);

  let files = $state<S3FileEntry[]>([]);
  let subFiles = $state<Record<string, S3FileEntry[]>>({});
  let loadingFiles = $state(false);
  let selectedFile = $state<string | null>(null);

  let fileContent = $state<unknown>(null);
  let loadingContent = $state(false);
  let summary = $state<ExecutionSummaryData | null>(null);

  let viewMode = $state<'json' | 'markdown'>('json');

  // Known LLM prompt/response field names that contain real markdown
  const MD_FIELD_NAMES = new Set([
    'kw_gen_user_message', 'kw_gen_raw_response',
    'kw_val_user_message', 'kw_val_raw_response',
    'summary_user_message', 'summary_raw_response',
  ]);

  function extractMarkdownFields(data: any, path = ''): { label: string; content: string }[] {
    const fields: { label: string; content: string }[] = [];
    if (!data || typeof data !== 'object') return fields;
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string' && MD_FIELD_NAMES.has(key) && value.length > 50) {
        fields.push({ label: key, content: value });
      } else if (typeof value === 'object' && value !== null) {
        fields.push(...extractMarkdownFields(value, path ? `${path}.${key}` : key));
      }
    }
    return fields;
  }

  const mdFields = $derived(fileContent ? extractMarkdownFields(fileContent) : []);
  const hasMarkdown = $derived(mdFields.length > 0);

  const breadcrumbs = [{ label: 'S3 Explorer' }] as const;

  // Resizable left panel
  let containerEl: HTMLElement;
  let leftWidth = $state(320);
  let filesWidth = $state(260);
  let dragging = $state<'left' | 'files' | null>(null);
  const MIN_LEFT = 200;
  const MIN_FILES = 180;

  function onDividerPointerDown(which: 'left' | 'files', e: PointerEvent) {
    e.preventDefault();
    dragging = which;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragging || !containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (dragging === 'left') {
      leftWidth = Math.max(MIN_LEFT, Math.min(x, rect.width - 500));
    } else if (dragging === 'files') {
      const filesStart = leftWidth + 14; // left panel + divider
      filesWidth = Math.max(MIN_FILES, Math.min(x - filesStart, rect.width - leftWidth - 300));
    }
  }

  function onPointerUp() {
    dragging = null;
  }

  async function loadExecutions() {
    loadingExecs = true;
    selectedExecId = null;
    files = [];
    subFiles = {};
    selectedFile = null;
    fileContent = null;
    summary = null;
    try {
      executions = await listExecutions(env);
    } finally {
      loadingExecs = false;
    }
  }

  async function handleSearchExecution(id: string) {
    const found = await searchExecution(env, id);
    if (found.length > 0) {
      if (!executions.some(e => e.id === id)) {
        executions = [{ id, lastModified: new Date().toISOString(), nodeCount: found.length, status: 'unknown' }, ...executions];
      }
      selectExecution(id);
    }
  }

  async function selectExecution(id: string) {
    selectedExecId = id;
    selectedFile = null;
    fileContent = null;
    summary = null;
    subFiles = {};
    loadingFiles = true;
    try {
      const [fileList, summaryData] = await Promise.all([
        listFiles(env, id),
        getExecutionSummary(env, id),
      ]);
      files = fileList;
      summary = summaryData;
    } finally {
      loadingFiles = false;
    }
  }

  async function expandDir(dir: string) {
    if (!selectedExecId) return;
    const dirFiles = await listFiles(env, selectedExecId, dir);
    subFiles = { ...subFiles, [dir]: dirFiles };
  }

  async function selectFile(key: string) {
    if (!selectedExecId) return;
    selectedFile = key;
    viewMode = 'json';
    loadingContent = true;
    try {
      fileContent = await getFileContent(env, selectedExecId, key);
    } finally {
      loadingContent = false;
    }
  }

  function handleNodeClick(nodeId: string) {
    const fileKey = `${nodeId}.json`;
    const match = files.find(f => f.key === fileKey);
    if (match) selectFile(fileKey);
  }

  // Load on mount and env change
  $effect(() => {
    void loadExecutions();
  });
</script>

<ToolPageLayout
  title="S3 Explorer"
  description="Browse agent execution logs and graph traces"
  breadcrumbs={[...breadcrumbs]}
>
  {#snippet content()}
    <!-- Env switcher -->
    <div class="flex justify-end mb-4">
      <EnvSwitcher bind:value={env} />
    </div>

    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      bind:this={containerEl}
      class="flex h-[calc(100vh-180px)] select-none"
      class:cursor-col-resize={!!dragging}
      onpointermove={onPointerMove}
      onpointerup={onPointerUp}
      onpointerleave={onPointerUp}
    >
      <!-- Left panel: execution list -->
      <div class="shrink-0 bg-bg-panel border border-border rounded-xl overflow-hidden flex flex-col" style="width: {leftWidth}px">
        <div class="px-3 py-2 border-b border-border">
          <h2 class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Executions</h2>
        </div>
        <ExecutionList
          {executions}
          selectedId={selectedExecId}
          onselect={selectExecution}
          onsearch={handleSearchExecution}
          loading={loadingExecs}
        />
      </div>

      <!-- Resize handle -->
      <div
        class="shrink-0 w-1.5 cursor-col-resize group relative mx-1"
        onpointerdown={(e) => onDividerPointerDown('left', e)}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize executions panel"
        tabindex={0}
      >
        <div class="absolute inset-y-0 left-0 w-full bg-border transition-colors duration-150 rounded {dragging === 'left' ? 'bg-accent' : 'group-hover:bg-accent/50'}"></div>
      </div>

      <!-- Right panel: file browser + viewer -->
      <div class="flex-1 min-w-0 flex flex-col gap-4">
        {#if !selectedExecId}
          <!-- Empty state -->
          <div class="flex-1 flex items-center justify-center bg-bg-panel border border-border rounded-xl">
            <div class="text-center">
              <p class="text-3xl mb-3">📂</p>
              <p class="text-sm text-text-secondary">Select an execution to browse files</p>
              <p class="text-xs text-text-muted mt-1">{executions.length} executions available</p>
            </div>
          </div>
        {:else}
          <!-- Summary card -->
          {#if summary}
            <NodeSummaryCard {summary} onnodeclick={handleNodeClick} />
          {/if}

          <div class="flex-1 min-h-0 flex gap-4">
            <!-- File tree -->
            <div class="shrink-0 bg-bg-panel border border-border rounded-xl overflow-hidden flex flex-col" style="width: {filesWidth}px">
              <div class="px-3 py-2 border-b border-border">
                <h2 class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Files</h2>
              </div>
              <div class="flex-1 overflow-y-auto py-1">
                {#if loadingFiles}
                  <div class="p-3 space-y-2">
                    {#each Array(5) as _}
                      <div class="h-3 bg-bg-card rounded animate-skeleton-pulse"></div>
                    {/each}
                  </div>
                {:else}
                  <FileTree
                    {files}
                    {selectedFile}
                    onselect={selectFile}
                    onexpand={expandDir}
                    {subFiles}
                  />
                {/if}
              </div>
            </div>

            <!-- Files/Viewer resize handle -->
            <div
              class="shrink-0 w-1.5 cursor-col-resize group relative"
              onpointerdown={(e) => onDividerPointerDown('files', e)}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize files panel"
              tabindex={0}
            >
              <div class="absolute inset-y-0 left-0 w-full bg-border transition-colors duration-150 rounded {dragging === 'files' ? 'bg-accent' : 'group-hover:bg-accent/50'}"></div>
            </div>

            <!-- JSON viewer -->
            <div class="flex-1 min-w-0 bg-bg-panel border border-border rounded-xl overflow-hidden flex flex-col">
              <div class="px-3 py-2 border-b border-border flex items-center justify-between">
                <h2 class="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  {selectedFile ?? 'Viewer'}
                </h2>
                {#if selectedFile}
                  <div class="flex items-center gap-2">
                    {#if hasMarkdown}
                      <button
                        onclick={() => viewMode = viewMode === 'json' ? 'markdown' : 'json'}
                        class="text-xs px-2 py-0.5 rounded {viewMode === 'markdown' ? 'bg-accent text-white' : 'bg-bg-card text-text-muted hover:text-text-secondary'}"
                        title="Toggle markdown view"
                      >MD</button>
                    {/if}
                    <StatusBadge status="info" label={selectedFile.endsWith('.json') ? 'JSON' : 'File'} size="sm" />
                  </div>
                {/if}
              </div>
              <div class="flex-1 overflow-auto p-4">
                {#if loadingContent}
                  <div class="space-y-2">
                    {#each Array(8) as _}
                      <div class="h-3 bg-bg-card rounded animate-skeleton-pulse" style="width: {50 + Math.random() * 50}%"></div>
                    {/each}
                  </div>
                {:else if fileContent}
                  {#if viewMode === 'markdown' && hasMarkdown}
                    <div class="space-y-6">
                      {#each mdFields as field}
                        <div>
                          <h3 class="text-xs font-semibold text-accent mb-2 font-mono">{field.label}</h3>
                          <div class="bg-bg-dark rounded-lg p-4 border border-border">
                            <MarkdownViewer content={field.content} />
                          </div>
                        </div>
                      {/each}
                    </div>
                  {:else}
                    <JsonViewer data={fileContent} maxDepth={4} />
                  {/if}
                {:else}
                  <div class="flex items-center justify-center h-full text-text-muted text-sm">
                    Select a file to view its contents
                  </div>
                {/if}
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/snippet}
</ToolPageLayout>
